import { ReactNode } from 'react';
import { Paperclip, Warning } from '@coolblue-development/icons';
import { Box } from '@coolblue-development/freddy';

type ChipVariant = 'default' | 'loading' | 'error';

interface BaseAttachmentChipProps {
  fileName: string;
  trailing?: ReactNode;
  onClick?: () => void;
  dataTestId?: string;
  variant?: ChipVariant;
}

const ERROR_COLOR = '#D71F1F';

const BaseAttachmentChip = ({
  fileName,
  trailing,
  onClick,
  dataTestId,
  variant = 'default'
}: BaseAttachmentChipProps): JSX.Element => {
  const isInteractive = Boolean(onClick);
  const isError = variant === 'error';

  return (
    <Box
      onClick={onClick}
      data-testid={dataTestId}
      title={fileName}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: '250px',
        maxWidth: '250px',
        minHeight: '40px',
        padding: '6px 12px',
        border: `1px solid ${isError ? ERROR_COLOR : '#DDDDDD'}`,
        borderRadius: '4px',
        backgroundColor: '#FFFFFF',
        opacity: variant === 'loading' ? 0.6 : 1,
        cursor: isInteractive ? 'pointer' : 'default',
        gap: '8px',
        '&:hover': { backgroundColor: isInteractive ? '#F2F7FC' : '#FFFFFF' }
      }}
    >
      <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
        {isError ? <Warning color={ERROR_COLOR} /> : <Paperclip color="#868A90" />}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: '14px',
          color: isError ? ERROR_COLOR : '#666666',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {fileName}
      </Box>

      {trailing && <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{trailing}</Box>}
    </Box>
  );
};

export default BaseAttachmentChip;
